import { useRef } from "react";
import { KIST_QR, ROLES } from "../constants";
import { commitInscan } from "../services/inscanTransaction";
import { commitSecureUitscan } from "../services/secureBusinessWrites";
import { logger } from "../utils/logger.js";
import { toJsDate } from "../utils/rijHelpers.js";
import {
  getOverwriteAction,
  OVERWRITE_ACTION,
} from "../utils/overwriteMode.js";

export default function useKisten({
  partijInfo,
  partijGestart,
  currentUser,
  canScan,
  toast,
  playShortErrorBeep,
  playErrorBeep,
  playSuccessBeep,
  setBlockingError,
  setAlertOk,
  it,
  manualModeActive,
  overrideMengPartijen,
  onRijVol,
  onKistIngescand,
  confirmFn,
}) {
  const scanInProgressRef = useRef(false);

  const scanKisten = async (input) => {
    if (!canScan) {
      toast?.error(it.errors.noRights);
      return { ok: false, reason: "no-rights" };
    }
    if (!input) return { ok: false, reason: "empty-input" };
    if (scanInProgressRef.current) {
      toast?.warn(
        it.errors.scanInProgress ||
          "De vorige scan wordt nog verwerkt. Wacht een moment."
      );
      return { ok: false, reason: "scan-in-progress" };
    }

    const { snijgroep, perceel, gewicht, soort, koelcel, rij } =
      partijInfo || {};
    if (!snijgroep || !perceel || !gewicht || !soort || !koelcel || !rij) {
      playShortErrorBeep?.();
      toast?.warn(it.errors.partijIncomplete);
      return { ok: false, reason: "party-incomplete" };
    }

    const barcodes = String(input)
      .split(",")
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    if (!barcodes.length) return { ok: false, reason: "empty-input" };

    const showTransactionError = (result, barcode) => {
      switch (result?.reason) {
        case "qr-not-recognized":
          toast?.error(it.errors.qrNotRecognized(barcode));
          break;
        case "wood-already-used":
          toast?.error(it.errors.woodAlreadyUsed(barcode));
          break;
        case "already-in-same-party":
          toast?.info(
            it.errors.alreadyInSameBatch({
              barcode,
              rij: result.existingCrate?.rij,
              koelcel: result.existingCrate?.koelcel,
            })
          );
          break;
        case "overwrite-requires-plus":
          toast?.warn(it.confirm.overwriteonlyplus(barcode));
          break;
        case "inventory-changed":
          toast?.warn(
            "De kist is ondertussen gewijzigd. Scan opnieuw om de actuele situatie te bevestigen."
          );
          break;
        case "row-not-found":
          toast?.error(it.errors.rowNotFound);
          break;
        case "row-manually-occupied":
          toast?.warn(
            it.errors.rowManuallyOccupied?.({ koelcel, rij }) ||
              "Deze rij is handmatig als bezet gemarkeerd."
          );
          break;
        case "other-party":
          toast?.warn(it.errors.rowOtherPartij({ koelcel, rij }));
          break;
        case "row-full":
          toast?.warn(it.errors.rowFull({ koelcel, rij }));
          break;
        case "warm-neighbour":
          toast?.warn(it.errors.warmNeighbour);
          break;
        default:
          toast?.error(
            it.errors.inscanFailed ||
              "Inscannen is mislukt. Probeer de kist opnieuw te scannen."
          );
      }
    };

    const confirmOverwrite = async (result, barcode) => {
      const existingCrate = result.existingCrate || {};
      const existingDate = toJsDate(existingCrate.datumIn);
      const inscanInfo =
        existingDate && !Number.isNaN(existingDate.getTime())
          ? `\n${it.confirm.inscanDateLabel || "Ingescand op"}: ` +
            existingDate.toLocaleString("nl-NL", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "";

      return confirmFn(
        it.confirm.overwriteMessage({
          barcode,
          partij: existingCrate.partij,
          inscanInfo,
          huidigePartij: `${perceel}-${soort}-${gewicht}-${snijgroep}`,
        }),
        it.confirm.overwriteTitle
      );
    };

    scanInProgressRef.current = true;
    let successfulScans = 0;

    try {
      for (const barcode of barcodes) {
        if (!KIST_QR.test(barcode)) {
          playShortErrorBeep?.();
          toast?.error(it.errors.invalidFormat(barcode));
          continue;
        }

        let result = await commitInscan({
          barcode,
          partijInfo,
          manualMixOverride: overrideMengPartijen,
        });
        const overwriteAction = getOverwriteAction(
          result.reason,
          manualModeActive
        );
        if (overwriteAction === OVERWRITE_ACTION.MANUAL_REQUIRED) {
          playShortErrorBeep?.();
          toast?.warn(it.confirm.overwriteManualOnly(barcode));
          continue;
        }
        if (overwriteAction === OVERWRITE_ACTION.CONFIRM) {
          const confirmed = await confirmOverwrite(result, barcode);
          if (!confirmed) continue;
          result = await commitInscan({
            barcode,
            partijInfo,
            manualMixOverride: overrideMengPartijen,
            confirmedInventoryFingerprint: result.inventoryFingerprint,
          });
        }
        if (!result.ok) {
          playShortErrorBeep?.();
          showTransactionError(result, barcode);
          continue;
        }

        successfulScans += 1;
        try {
          playSuccessBeep?.();
        } catch (error) {
          logger.error("playSuccessBeep failed in scanKisten", error);
        }
        setAlertOk?.({
          barcode,
          message:
            it?.crateAddedToRow?.({ barcode, rij: result.row.rij }) ||
            `${barcode} toegevoegd aan rij ${result.row.rij}.`,
        });
        try {
          onKistIngescand?.(result.newCrate);
        } catch (error) {
          logger.error("onKistIngescand callback error", error);
        }

        if (result.becameFull && typeof onRijVol === "function") {
          await onRijVol({
            snijgroep,
            perceel,
            gewicht,
            soort,
            koelcel,
            rij,
            laatsteKist: result.newCrate,
          });
          break;
        }
      }
      return { ok: successfulScans > 0, processed: successfulScans };
    } catch (error) {
      logger.error("Beveiligd inscannen mislukt", error);
      playShortErrorBeep?.();
      toast?.error(
        it.errors.inscanFailed ||
          "Inscannen is mislukt. Probeer de kist opnieuw te scannen."
      );
      return { ok: false, reason: "transaction-failed" };
    } finally {
      scanInProgressRef.current = false;
    }
  };

  const uitscannen = async (input, ctx) => {
    const magUitscannen =
      !!currentUser &&
      [
        ROLES.UITSCANNER,
        ROLES.SUPERVISOR,
        ROLES.ADMIN,
      ].includes(currentUser.role);
    if (!magUitscannen) {
      playShortErrorBeep?.();
      toast?.error("Je hebt geen rechten om uit te scannen.");
      return { ok: false, reason: "no-rights" };
    }
    if (currentUser.role !== ROLES.UITSCANNER && partijGestart) {
      playShortErrorBeep?.();
      toast?.warn(
        "Eerst de huidige partij beëindigen voordat je kunt uitscannen!"
      );
      return { ok: false, reason: "active-party" };
    }

    const barcodes = String(input || "")
      .split(",")
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    if (!barcodes.length) return { ok: false, reason: "empty-input" };

    const overgebleven = [];
    let processed = 0;
    let firstFailure = null;
    for (const barcode of barcodes) {
      try {
        const result = await commitSecureUitscan({
          barcode,
          machineId: ctx?.machineId ?? null,
          machineGroep: ctx?.machineGroep ?? null,
          skipTemperatureCheck: ctx?.skipTemperatureCheck === true,
          crateLoanId: ctx?.crateLoanId ?? null,
        });
        if (!result?.ok) {
          overgebleven.push(barcode);
          firstFailure ||= result;
          if (result?.reason === "not-found") {
            toast?.info(`Barcode ${barcode} niet in voorraad gevonden.`);
          } else if (result?.reason === "row-not-cooled") {
            playErrorBeep?.();
            setBlockingError?.({
              open: true,
              message: "Rij is nog warm — uitscannen niet toegestaan.",
            });
          } else if (result?.reason === "machine-row-mismatch") {
            playErrorBeep?.();
            setBlockingError?.({
              open: true,
              message:
                "De kist staat niet in de voorste rij van deze machine. Vernieuw de machineplanning en scan opnieuw.",
            });
          } else if (result?.reason === "machine-cooldown") {
            // UitscannerView toont de waarschuwing in de gekozen taal.
          } else {
            toast?.error("Uitscannen is mislukt.");
          }
          continue;
        }

        processed += 1;
        playSuccessBeep?.();
        setAlertOk?.({
          barcode,
          message: `Succesvol uitgescand (Machine ${ctx?.machineId ?? "?"})`,
        });
      } catch (error) {
        logger.error("Beveiligd uitscannen mislukt", { barcode, error });
        overgebleven.push(barcode);
        toast?.error("Uitscannen is mislukt. Probeer opnieuw.");
      }
    }
    return {
      ok: processed > 0,
      processed,
      overgebleven,
      ...(processed === 0 && firstFailure ? firstFailure : {}),
    };
  };

  return { scanKisten, uitscannen };
}
